import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import BoostModal from '@/components/BoostModal';
import ListingCard from '@/components/ListingCard';
import { ListingDetailSkeleton } from '@/components/Skeletons';
import api from '@/lib/api';
import SEO from '@/components/SEO';
import { toast } from 'sonner';
import {
  MapPin, Truck, PackageCheck, FileCheck, Shield, Calendar, Eye,
  MessageSquare, Rocket, ArrowLeft, Star, ChevronLeft, ChevronRight, Heart, Store, Share2, Copy, MessageCircle, Mail, Tag,
} from 'lucide-react';

const conditionColors = {
  neuf: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  occasion: 'bg-amber-100 text-amber-700 border-amber-200',
  reconditionne: 'bg-purple-100 text-purple-700 border-purple-200',
};

const conditionDescriptions = {
  fr: {
    neuf: 'Produit neuf, encore dans son emballage d\'origine',
    occasion: 'Produit déballé, stocké ou déjà utilisé',
    reconditionne: 'Produit remis à neuf par un professionnel',
  },
  nl: {
    neuf: 'Nieuw product, nog in originele verpakking',
    occasion: 'Uitgepakt, opgeslagen of eerder gebruikt product',
    reconditionne: 'Professioneel opgeknapt product',
  },
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tc, lang } = useTranslation('listingDetail');
  const locale = lang === 'nl' ? 'nl-NL' : 'fr-FR';
  const { t: tCond } = useTranslation('conditions');
  const { t: tSector } = useTranslation('sectors');
  const { t: tShare } = useTranslation('share');
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [sellerListings, setSellerListings] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerSending, setOfferSending] = useState(false);

  const shareUrl = `https://www.destockpro.be/annonces/${id}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success(tShare('linkCopied'));
    setShowShare(false);
  };

  const handleShareWhatsApp = () => {
    const price = listing?.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' });
    const text = tShare('whatsappText').replace('{title}', listing?.title).replace('{price}', price);
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + shareUrl)}`, '_blank');
    setShowShare(false);
  };

  const handleShareEmail = () => {
    const price = listing?.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' });
    const subject = tShare('emailSubject').replace('{title}', listing?.title);
    const body = tShare('emailBody').replace('{title}', listing?.title).replace('{price}', price).replace('{url}', shareUrl);
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const a = document.createElement('a');
    a.href = mailtoUrl;
    a.target = '_self';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setShowShare(false);
  };

  useEffect(() => {
    api.get(`/listings/${id}`)
      .then((r) => {
        setListing(r.data);
        // Fetch seller's other listings
        if (r.data.seller_id) {
          api.get(`/listings/seller/${r.data.seller_id}?exclude=${id}`)
            .then(sr => setSellerListings(sr.data.listings || []))
            .catch(() => {});
        }
      })
      .catch(() => setListing(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user) {
      api.get('/favorites/ids').then(r => {
        const ids = Array.isArray(r.data) ? r.data : [];
        setIsFavorited(ids.includes(id));
      }).catch(() => {});
    }
  }, [user, id]);

  const toggleFavorite = async () => {
    if (!user) { navigate('/connexion'); return; }
    try {
      const { data } = await api.post(`/favorites/${id}`);
      setIsFavorited(data.favorited);
      toast.success(data.favorited ? t('savedToast') : t('removedToast'));
    } catch (err) { console.error('Favorite toggle failed:', err); }
  };

  const handleContact = async () => {
    if (!user) {
      navigate('/connexion');
      return;
    }
    if (user.id === listing.seller_id) {
      toast.error(t('selfContact'));
      return;
    }
    try {
      const res = await api.post('/conversations', {
        seller_id: listing.seller_id,
        listing_id: listing.id,
      });
      navigate(`/messages?conversation=${res.data.id}`);
    } catch {
      toast.error(t('conversationError'));
    }
  };

  const handleMakeOffer = async () => {
    if (!user) { navigate('/connexion'); return; }
    if (!offerPrice || parseFloat(offerPrice) <= 0) return;
    setOfferSending(true);
    try {
      const res = await api.post('/conversations', {
        seller_id: listing.seller_id,
        listing_id: listing.id,
      });
      const formattedPrice = parseFloat(offerPrice).toLocaleString(locale, { style: 'currency', currency: 'EUR' });
      await api.post(`/conversations/${res.data.id}/messages`, {
        content: t('offerMessage').replace('{price}', formattedPrice).replace('{title}', listing.title),
      });
      setShowOffer(false);
      setOfferPrice('');
      navigate(`/messages?conversation=${res.data.id}`);
    } catch {
      toast.error(t('conversationError'));
    } finally {
      setOfferSending(false);
    }
  };

  if (loading) {
    return <ListingDetailSkeleton />;
  }

  if (!listing) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="listing-not-found">
        <p className="text-lg font-semibold text-neutral-700">{t('notFoundError')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/annonces')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToListings')}
        </Button>
      </div>
    );
  }

  const images = listing.images?.length > 0
    ? listing.images.map((img) => img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`)
    : ['https://images.unsplash.com/photo-1631856956334-35db20cb7748?w=800&h=600&fit=crop'];
  const isOwner = user && user.id === listing.seller_id;

  const details = [
    { label: t('condition'), value: listing.condition },
    { label: t('brand'), value: listing.brand },
    { label: t('reference'), value: listing.reference },
    { label: t('quantity'), value: listing.quantity },
    { label: t('sector'), value: listing.sector ? tSector(listing.sector) : null },
    { label: t('category'), value: listing.category },
  ].filter((d) => d.value);

  const imgSrc = listing.images?.[0]
    ? (listing.images[0].startsWith('http') ? listing.images[0] : `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`)
    : null;

  return (
    <div className="site-container py-8" data-testid="listing-detail-page">
      <SEO
        title={`${listing.title} - ${listing.price}€`}
        description={listing.description ? listing.description.substring(0, 160) : `${listing.title} à ${listing.price}€ sur DestockPro. Matériel professionnel de déstockage.`}
        path={`/annonces/${listing.id}`}
        image={imgSrc}
        type="product"
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": listing.title,
          "description": listing.description || listing.title,
          "image": imgSrc || undefined,
          "offers": {
            "@type": "Offer",
            "price": listing.price,
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          },
          "brand": listing.brand ? { "@type": "Brand", "name": listing.brand } : undefined
        }}
      />
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-6">
        <Link to="/annonces" className="hover:text-[#1F3F72] transition-colors" data-testid="back-to-listings">
          <ArrowLeft className="w-4 h-4 inline mr-1" />{t('backToListings')}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Mobile only: Title + Price above photo */}
        <div className="lg:hidden space-y-2" data-testid="mobile-header">
          <h1 className="font-heading text-xl font-bold text-neutral-900 break-words">{listing.title}</h1>
          <p className="text-2xl font-bold text-[#F25C05]">
            {listing.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}
            <span className="text-xs font-semibold text-neutral-400 ml-1">{tc('vatExcl')}</span>
            <span className="text-sm font-normal text-neutral-500 ml-1">/ {tc('unit')}</span>
          </p>
          {listing.quantity > 1 && (
            <p className="text-sm text-neutral-500">{listing.quantity} {tc('available')}</p>
          )}
        </div>

        {/* Left: images + description */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image gallery */}
          <div className="relative rounded-xl overflow-hidden bg-neutral-100 aspect-[4/3]" data-testid="listing-image-gallery">
            <img src={images[currentImage]} alt={listing.title} className="w-full h-full object-contain" />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImage((prev) => (prev > 0 ? prev - 1 : images.length - 1))}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow hover:bg-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentImage((prev) => (prev < images.length - 1 ? prev + 1 : 0))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow hover:bg-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setCurrentImage(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === currentImage ? 'bg-[#F25C05]' : 'bg-white/70'}`}
                    />
                  ))}
                </div>
              </>
            )}
            {/* Badges on image */}
            <div className="absolute top-3 left-3 flex gap-2">
              {listing.active_boosts?.badge && (
                <Badge className="bg-[#F25C05] text-white text-xs font-semibold">
                  <Star className="w-3 h-3 mr-1 fill-current" /> {tc('topAd')}
                </Badge>
              )}
            </div>
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setCurrentImage(i)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${i === currentImage ? 'border-[#F25C05]' : 'border-neutral-200 hover:border-neutral-400'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Description */}
          <div className="bg-white border rounded-xl p-6" data-testid="listing-description-section">
            <h2 className="font-heading text-lg font-semibold text-neutral-900 mb-3">{t('description')}</h2>
            <p className="text-sm text-neutral-600 leading-relaxed whitespace-pre-line">{listing.description}</p>
          </div>

          {/* Details */}
          {details.length > 0 && (
            <div className="bg-white border rounded-xl p-6" data-testid="listing-details-section">
              <h2 className="font-heading text-lg font-semibold text-neutral-900 mb-4">{t('details')}</h2>
              <div className="grid grid-cols-2 gap-3">
                {details.map((d) => (
                  <DetailRow key={d.label} label={d.label} value={d.value} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Price card */}
          <div className="bg-white border rounded-xl p-6 sticky top-20" data-testid="listing-sidebar">
            <h1 className="hidden lg:block font-heading text-xl font-bold text-neutral-900 mb-2 break-words">{listing.title}</h1>
            <p className="hidden lg:block text-3xl font-bold text-[#F25C05] mb-1">
              {listing.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}
              <span className="text-sm font-semibold text-neutral-400 ml-1.5">{tc('vatExcl')}</span>
              <span className="text-sm font-normal text-neutral-500 ml-1">/ {tc('unit')}</span>
            </p>
            <div className="hidden lg:block">
              {listing.quantity > 1 && (
                <p className="text-sm text-neutral-500 mb-4">{listing.quantity} {tc('available')}</p>
              )}
              {(!listing.quantity || listing.quantity <= 1) && <div className="mb-4" />}
            </div>


            {/* Options */}
            <div className="flex flex-wrap gap-2 mb-4">
              {listing.delivery && <OptionBadge icon={Truck} label={tc('deliveryPossible')} />}
              {listing.pickup && <OptionBadge icon={PackageCheck} label={tc('pickupOnSite')} />}
              {listing.invoice && <OptionBadge icon={FileCheck} label={tc('invoice')} />}
              {listing.warranty && <OptionBadge icon={Shield} label={tc('warranty')} />}
            </div>

            {listing.location && (
              <p className="flex items-center gap-1.5 text-sm text-neutral-500 mb-2">
                <MapPin className="w-4 h-4" /> {listing.location}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-neutral-400 mb-6">
              {listing.created_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {t('publishedOn')} {new Date(listing.created_at).toLocaleDateString(locale)}
                </span>
              )}
              {listing.views !== undefined && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {listing.views} {t('views')}
                </span>
              )}
            </div>

            {/* Actions */}
            {!isOwner && (
              <>
                <Button
                  className="w-full bg-[#F25C05] hover:bg-[#d94f04] text-white mb-3"
                  onClick={handleContact}
                  data-testid="contact-seller-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> {t('contactSeller')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full mb-3 border-[#1F3F72] text-[#1F3F72] hover:bg-[#1F3F72]/5"
                  onClick={() => { if (!user) { navigate('/connexion'); return; } setShowOffer(true); setOfferPrice(''); }}
                  data-testid="make-offer-btn"
                >
                  <Tag className="w-4 h-4 mr-2" /> {t('makeOffer')}
                </Button>
                <Button
                  variant="outline"
                  className={`w-full mb-3 ${isFavorited ? 'border-red-300 text-red-500 hover:bg-red-50' : ''}`}
                  onClick={toggleFavorite}
                  data-testid="favorite-listing-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavorited ? 'fill-current text-red-500' : ''}`} />
                  {isFavorited ? t('saved') : t('save')}
                </Button>
              </>
            )}

            {/* Share Button */}
            <div className="relative">
              <Button
                variant="outline"
                className="w-full mb-3"
                onClick={() => setShowShare(!showShare)}
                data-testid="share-listing-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> {tShare('share')}
              </Button>
              {showShare && (
                <div className="absolute left-0 right-0 z-10 bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-lg shadow-lg p-2 space-y-1 mt-1" data-testid="share-dropdown">
                  <button onClick={handleCopyLink} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-md transition-colors" data-testid="share-copy-link">
                    <Copy className="w-4 h-4 text-neutral-400" /> {tShare('copyLink')}
                  </button>
                  <button onClick={handleShareWhatsApp} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-md transition-colors" data-testid="share-whatsapp">
                    <MessageCircle className="w-4 h-4 text-green-500" /> WhatsApp
                  </button>
                  <button onClick={handleShareEmail} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-md transition-colors" data-testid="share-email">
                    <Mail className="w-4 h-4 text-blue-500" /> Email
                  </button>
                </div>
              )}
            </div>

            {isOwner && (
              <>
                <Button
                  className="w-full bg-[#1F3F72] hover:bg-[#19325B] text-white mb-3"
                  onClick={() => setShowBoostModal(true)}
                  data-testid="boost-listing-btn"
                >
                  <Rocket className="w-4 h-4 mr-2" /> {t('boostListing')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/annonces/${listing.id}/modifier`)}
                  data-testid="edit-listing-btn"
                >
                  {tc('edit')}
                </Button>
              </>
            )}

            {/* Seller info */}
            <div className="border-t mt-6 pt-4" data-testid="seller-info">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1F3F72] text-white flex items-center justify-center font-semibold text-sm">
                  {(listing.seller_company || listing.seller_first_name || '?')[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-heading text-sm font-semibold text-neutral-900">
                    {listing.seller_company || listing.seller_first_name || t('seller')}
                  </p>
                  {listing.seller_company && user && (
                    <p className="text-xs text-neutral-500">{listing.seller_first_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {listing.seller_verified && <VerifiedBadge size="xs" />}
                {listing.seller_online && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {t('onlineStatus')}
                  </span>
                )}
              </div>

              {/* CTA: login to contact or send message */}
              {!isOwner && !user && (
                <Button
                  variant="outline"
                  className="w-full mt-4 border-[#1F3F72] text-[#1F3F72] hover:bg-[#1F3F72]/5"
                  onClick={() => navigate('/connexion')}
                  data-testid="login-to-contact-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> {t('loginToContact')}
                </Button>
              )}

              {sellerListings.length > 0 && (
                <Link
                  to={`/annonces?seller=${listing.seller_id}`}
                  className="flex items-center gap-1.5 mt-4 text-sm font-medium text-[#1F3F72] hover:text-[#F25C05] transition-colors"
                  data-testid="see-seller-listings-link"
                >
                  <Store className="w-4 h-4" /> {t('otherListingsCount').replace('{count}', sellerListings.length)}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Seller's other listings */}
      {sellerListings.length > 0 && (
        <div className="mt-10" data-testid="seller-other-listings">
          <h2 className="font-heading text-lg font-bold text-neutral-900 mb-4">
            {t('otherSellerListings')} {listing.seller_company || listing.seller_first_name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sellerListings.slice(0, 4).map(sl => (
              <ListingCard key={sl.id} listing={sl} />
            ))}
          </div>
          {sellerListings.length > 4 && (
            <div className="text-center mt-4">
              <Link to={`/annonces?seller=${listing.seller_id}`} className="text-sm font-medium text-[#1F3F72] hover:text-[#F25C05]">
                {t('viewAllSellerListings')} &rarr;
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Boost Modal */}
      {showBoostModal && <BoostModal listing={listing} onClose={() => setShowBoostModal(false)} />}

      {/* Make Offer Modal */}
      {showOffer && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="offer-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowOffer(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 z-10">
            <h3 className="text-lg font-bold text-neutral-900 mb-1">{t('makeOffer')}</h3>
            <p className="text-sm text-neutral-500 mb-4">
              {t('offerSubtitle')} <span className="font-semibold text-[#F25C05]">{listing.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}</span>
            </p>
            <div className="relative mb-4">
              <input
                type="number"
                step="0.01"
                min="0"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                placeholder={t('offerPlaceholder')}
                className="w-full rounded-lg border border-neutral-300 px-4 py-3 pr-12 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#1F3F72] focus:border-transparent"
                autoFocus
                data-testid="offer-price-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 font-medium">€</span>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowOffer(false)} data-testid="offer-cancel-btn">
                {tc('cancel')}
              </Button>
              <Button
                className="flex-1 bg-[#1F3F72] hover:bg-[#16305a] text-white"
                onClick={handleMakeOffer}
                disabled={!offerPrice || parseFloat(offerPrice) <= 0 || offerSending}
                data-testid="offer-submit-btn"
              >
                <Tag className="w-4 h-4 mr-2" /> {offerSending ? '...' : t('sendOffer')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OptionBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-neutral-600 bg-neutral-50 border rounded-md px-2.5 py-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-neutral-500">{label}:</span>
      <span className="font-medium text-neutral-900">{value}</span>
    </div>
  );
}
